import {
  isEnrichedShift,
  type RouteAssignment, type ExistingShiftStep, type EnrichedExistingShift,
} from '@/api/route-plans';

// Single owner of the rule "interleave new assignments (arrivalTime) with existing
// shifts (startTime) chronologically and number them sequentially" — the sequence
// dispatchers review before applying a plan. The list, map and timeline all consume
// these helpers so their step numbering and travel gaps can never disagree.

/** Stable key for an assignment step, independent of object identity. */
export function assignmentStepKey(assignment: RouteAssignment): string {
  return `a-${assignment.id}`;
}

/**
 * Stable key for an existing-shift step. Legacy plans (no shift_id_map) carry basic
 * time blocks without ids, keyed by their index within the caregiver's shift array.
 */
export function existingShiftStepKey(shift: ExistingShiftStep, index = 0): string {
  return isEnrichedShift(shift) ? `s-${shift.shiftId}` : `b-${index}`;
}

export type MergedRouteStep =
  | { kind: 'assignment'; key: string; startTime: string; endTime: string;
      displaySeq: number; assignment: RouteAssignment }
  | { kind: 'existing'; key: string; startTime: string; endTime: string;
      displaySeq: number; shift: EnrichedExistingShift };

/**
 * Chronological steps for one caregiver: assignments plus enriched existing shifts,
 * sorted by start and numbered 1..n. Legacy basic shifts carry no identity or route
 * position, so they are excluded from the displayed sequence.
 */
export function mergeRouteSteps(
  assignments: RouteAssignment[], existingShifts: ExistingShiftStep[],
): MergedRouteStep[] {
  const fromAssignments: MergedRouteStep[] = assignments.map(assignment => ({
    kind: 'assignment' as const,
    key: assignmentStepKey(assignment),
    startTime: assignment.arrivalTime,
    endTime: assignment.departureTime,
    displaySeq: 0,
    assignment,
  }));
  const fromShifts: MergedRouteStep[] = existingShifts
    .filter(isEnrichedShift)
    .map(shift => ({
      kind: 'existing' as const,
      key: existingShiftStepKey(shift),
      startTime: shift.startTime,
      endTime: shift.endTime,
      displaySeq: 0,
      shift,
    }));

  const merged = [...fromAssignments, ...fromShifts]
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  merged.forEach((step, index) => {
    step.displaySeq = index + 1;
  });

  return merged;
}

/** { stepKey => displaySeq } for views that look the sequence up per source object. */
export function displaySeqByKey(
  assignments: RouteAssignment[], existingShifts: ExistingShiftStep[],
): Record<string, number> {
  return Object.fromEntries(
    mergeRouteSteps(assignments, existingShifts).map(step => [step.key, step.displaySeq]),
  );
}

export interface TravelGap {
  startMin: number;
  endMin: number;
  humanTravelTime?: string | null;
}

const MINUTES_PER_HOUR = 60;

function timeToMinutes(timeStr: string): number {
  const date = new Date(timeStr);
  if (Number.isNaN(date.getTime())) return 0;

  return date.getHours() * MINUTES_PER_HOUR + date.getMinutes();
}

/**
 * Gaps between consecutive entries (assignments plus ALL existing shifts, legacy
 * included), keyed by the FOLLOWING step's stable key. Touching or overlapping
 * entries produce no gap.
 */
export function buildTravelGaps(
  assignments: RouteAssignment[], existingShifts: ExistingShiftStep[],
): Map<string, TravelGap> {
  const entries = [
    ...assignments.map(assignment => ({
      key: assignmentStepKey(assignment),
      startTime: assignment.arrivalTime,
      endTime: assignment.departureTime,
      humanTravelTime: assignment.humanTravelTime,
    })),
    ...existingShifts.map((shift, index) => ({
      key: existingShiftStepKey(shift, index),
      startTime: shift.startTime,
      endTime: shift.endTime,
      humanTravelTime: isEnrichedShift(shift) ? shift.humanTravelTime : null,
    })),
  ].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const gaps = new Map<string, TravelGap>();
  for (let i = 1; i < entries.length; i++) {
    const entry = entries[i]!;
    const prevEnd = timeToMinutes(entries[i - 1]!.endTime);
    const arrival = timeToMinutes(entry.startTime);
    if (arrival <= prevEnd) continue;
    gaps.set(entry.key, {
      startMin: prevEnd, endMin: arrival, humanTravelTime: entry.humanTravelTime,
    });
  }

  return gaps;
}
